import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '../typography/Typography';
import { IconButton } from '../button/IconButton';
import { ArrowBack } from '../private/icons/ArrowBack';
import { ArrowForward } from '../private/icons/ArrowForward';
import { Dropdown } from '../dropdown/Dropdown';
import { MenuItem } from '../menu/MenuItem';

/**
 * A set of controls used to paginate over the rows of a [`Table`](#table) component.
 *
 * It renders two `IconButton`s to navigate to the previous and next page and
 * a `Dropdown` to select the number of rows per page to show.
 *
 * Related components:
 * [TableFooterShowMore](#tablefootershowmore), [TableRow](#tablerow), [TableCell](#tablecell),
 * [IconButton](#iconbutton), [Dropdown](#dropdown)
 *
 *
 * Usage:
 *
 * ```jsx
 * import { TableFooterPagination } from '@one-thd/sui-atomic-components';
 * ```
 */
const TableFooterPagination = React.forwardRef((props, ref) => {
  const {
    count,
    disableViewFifty = false,
    disableViewAll = false,
    page = 0,
    rowsPerPage: rowsPerPageProp = 10,
    onRowsPerPageChange,
    onPageChange,
    dropdownProps = {},
    DropdownProps: DropdownPropsProp = {},
    previousIconButtonProps,
    nextIconButtonProps,
    ...other
  } = props;

  const [rowsPerPage, setRowsPerPage] = React.useState(rowsPerPageProp);

  const handleRowsPerPageChange = (event, newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    if (onRowsPerPageChange) {
      onRowsPerPageChange(event, newRowsPerPage);
    }
  };

  const handleBackButtonClick = (event) => {
    if (onPageChange) {
      onPageChange(event, page - 1);
    }
  };

  const handleNextButtonClick = (event) => {
    if (onPageChange) {
      onPageChange(event, page + 1);
    }
  };

  const from = page * rowsPerPage + 1;
  const to = Math.min(from + rowsPerPage - 1, count);

  const DropdownProps = {
    ...dropdownProps,
    ...DropdownPropsProp
  };

  return (
    <div
      ref={ref}
      className="sui-flex sui-gap-2 sui-justify-left sui-items-center"
      {...other}
    >
      <IconButton
        icon={ArrowBack}
        aria-label="Previous page"
        onClick={handleBackButtonClick}
        disabled={page === 0}
        {...previousIconButtonProps}
      />
      <IconButton
        icon={ArrowForward}
        aria-label="Next page"
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        {...nextIconButtonProps}
      />
      <div className="sui-w-32">
        <Dropdown
          aria-label="Row per page"
          value={rowsPerPage}
          onChange={handleRowsPerPageChange}
          {...DropdownProps}
        >
          {count > 10 && (<MenuItem value={10}>View 10</MenuItem>)}
          {count > 25 && (<MenuItem value={25}>View 25</MenuItem>)}
          {!disableViewFifty && count > 50 && (<MenuItem value={50}>View 50</MenuItem>)}
          {!disableViewAll && (<MenuItem value={count}>View All</MenuItem>)}
        </Dropdown>
      </div>
      <Typography color="subtle">{from} - {to} of {count}</Typography>
    </div>
  );

});

TableFooterPagination.displayName = 'TableFooterPagination';

TableFooterPagination.propTypes = {
  /**
   * The total number of rows
   */
  count: PropTypes.number.isRequired,
  /**
   * If `true`, the `View 50` menu option won't be displayed.
   */
  disableViewFifty: PropTypes.bool,
  /**
   * If `true`, the `View All` menu option won't be displayed.
   */
  disableViewAll: PropTypes.bool,
  /**
   * Props applied to the rows per page `Dropdown` element.
   *
   * This prop is an alias for the `DropdownProps` prop.
   * It's recommended to use the `DropdownProps` prop instead, as `dropdownProps` will be deprecated in the future.
   */
  dropdownProps: PropTypes.object,
  /**
   * The props used for the Dropdown component.
   * @default {}
   */
  DropdownProps: PropTypes.object,
  /**
  * Props applied to the next `IconButton` component.
  */
  nextIconButtonProps: PropTypes.object,
  /**
   * The zero-base index of the current page.
   * @default 0
   */
  page: PropTypes.number,
  /**
   * Props applied to the previous `IconButton` component.
   */
  previousIconButtonProps: PropTypes.object,
  /**
   * The number of rows per page.
   * @default 10
   */
  rowsPerPage: PropTypes.number,
  /**
   * Callback fired when the page is changed.
   */
  onPageChange: PropTypes.func,
  /**
   * Callback fired when the number of rows per page is changed.
   */
  onRowsPerPageChange: PropTypes.func
};

export { TableFooterPagination };
